/**
 * AuthContext — thin re-export layer over useAuthStore (Phase 0.2/0.5).
 *
 * HISTORICAL CONTEXT: this file previously did three things:
 *   1. Wrapped useAuthStore in a React Context (redundant — Zustand is
 *      already globally accessible via the useAuthStore() hook).
 *   2. Installed its own axios.interceptors on the global axios instance,
 *      duplicating what services/api.ts already does on the canonical
 *      api instance.
 *   3. Ran its own role-redirect useEffect, duplicating the RouteGuards
 *      logic.
 *
 * CURRENT STATE: compatibility shim. 20 consumer files still import
 * { useAuth } or { RequireAuth, NoAuth } from this path; we keep those
 * imports working while the real logic lives elsewhere:
 *   - State + methods → useAuthStore (Zustand, src/stores/authStore.ts)
 *   - Route guards    → src/components/auth/RouteGuards.tsx
 *   - 401 + refresh   → src/services/api.ts
 *
 * ───────────────────────────────────────────────────────────────────
 * DAY 14 LOOP FIX — why this file changed:
 *
 * The old AuthProvider destructured the ENTIRE store with no selector:
 *     const { isAuthenticated, user, ... isLoading, error } = useAuthStore();
 * That subscribes the provider to every field, so it re-rendered on every
 * `isLoading` flip. Combined with the bootstrap effect calling
 * fetchUserProfile (which flips isLoading), and RouteGuards rendering
 * <AuthLoadingState/> while isLoading=true (unmounting the whole route
 * tree), this produced a mount→fetch→isLoading→unmount→remount→fetch loop
 * that hammered /auth/me (and, on remount, /activity and /notifications)
 * several times per second.
 *
 * Two corrections:
 *   1. SELECTORS — each value is read with its own useAuthStore((s)=>s.x)
 *      selector. Actions are read once (stable references). The provider
 *      no longer re-renders on unrelated field changes, and never loops on
 *      isLoading.
 *   2. RUN-ONCE BOOTSTRAP — the rehydrate-time profile fetch is guarded by
 *      a ref so it fires at most once per provider lifetime, depending only
 *      on primitive values. Belt-and-suspenders with the store's in-flight
 *      guard on fetchUserProfile.
 * ───────────────────────────────────────────────────────────────────
 *
 * MIGRATION PATH: as each consumer file gets touched, replace
 *   `import { useAuth } from '@/context/AuthContext'`
 * with direct `useAuthStore((s) => s.user)` selectors. When all 20
 * consumers migrate, delete this file.
 */
import React, { createContext, useContext, useEffect, useRef } from "react";
import useAuthStore from "@/stores/authStore";

// Re-export RouteGuards from their new home so existing imports keep working.
export { RequireAuth, NoAuth } from "@/components/auth/RouteGuards";

// ─── Context type — mirrors the relevant slice of AuthState ──────────
interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: ReturnType<typeof useAuthStore.getState>["user"];
  token: ReturnType<typeof useAuthStore.getState>["token"];
  login: ReturnType<typeof useAuthStore.getState>["login"];
  logout: ReturnType<typeof useAuthStore.getState>["logout"];
  createCashier: ReturnType<typeof useAuthStore.getState>["createCashier"];
  resetPassword: ReturnType<typeof useAuthStore.getState>["resetPassword"];
  confirmResetPassword: ReturnType<
    typeof useAuthStore.getState
  >["confirmResetPassword"];
  updateProfile: ReturnType<typeof useAuthStore.getState>["updateProfile"];
  fetchUserProfile: ReturnType<
    typeof useAuthStore.getState
  >["fetchUserProfile"];
  refreshAuthToken: ReturnType<
    typeof useAuthStore.getState
  >["refreshAuthToken"];
  error: string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

/**
 * AuthProvider — wraps children, exposes auth slice via Context.
 *
 * Subscribes to useAuthStore via PER-FIELD SELECTORS (not whole-store
 * destructure) so re-renders are scoped to the fields actually used, and
 * an isLoading flip can't trigger a render→effect→fetch loop.
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // ── State selectors — each subscribes to ONE field ────────────────
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);
  const isLoading = useAuthStore((s) => s.isLoading);
  const error = useAuthStore((s) => s.error);

  // ── Action selectors — stable references, never cause re-renders ──
  const login = useAuthStore((s) => s.login);
  const logout = useAuthStore((s) => s.logout);
  const createCashier = useAuthStore((s) => s.createCashier);
  const resetPassword = useAuthStore((s) => s.resetPassword);
  const confirmResetPassword = useAuthStore((s) => s.confirmResetPassword);
  const updateProfile = useAuthStore((s) => s.updateProfile);
  const fetchUserProfile = useAuthStore((s) => s.fetchUserProfile);
  const refreshAuthToken = useAuthStore((s) => s.refreshAuthToken);

  // ── Run-once bootstrap ────────────────────────────────────────────
  // Covers the "page reload after persist rehydrated token but not user"
  // case. Guarded by a ref so it fires at most once per provider lifetime
  // — the dependency list is intentionally minimal (primitives only), and
  // the ref prevents re-fire even if those primitives churn during the
  // async fetch. The store's in-flight guard is the second line of
  // defense.
  const didBootstrap = useRef(false);
  useEffect(() => {
    if (didBootstrap.current) return;
    if (token && !user && isAuthenticated) {
      didBootstrap.current = true;
      void fetchUserProfile();
    }
    // fetchUserProfile is a stable Zustand action; safe to omit. We depend
    // on the primitive trio that gates the bootstrap condition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user, isAuthenticated]);

  const contextValue: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    token,
    login,
    logout,
    createCashier,
    resetPassword,
    confirmResetPassword,
    updateProfile,
    fetchUserProfile,
    refreshAuthToken,
    error,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

/**
 * Hook for consuming the auth context. Existing consumer files import
 * this from `@/context/AuthContext`. New code should prefer:
 *   const user = useAuthStore((s) => s.user);
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
